import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { apiService } from '../../services/apiService';

export const fetchPermissionData = createAsyncThunk(
    'maintenance/fetchPermissionData',
    async (params, { rejectWithValue }) => {
        try {
            const response = await apiService.getPermissionApi(params);
            return response;
        } catch (error) {
            return rejectWithValue(error || 'Failed to fetch Permission data');
        }
    }
);

export const fetchPermissionDropdown = createAsyncThunk(
    'maintenance/fetchPermissionDropdown',
    async (_, { rejectWithValue }) => {
        try {
            const response = await apiService.getPermissionDropdown();
            return response;
        } catch (error) {
            return rejectWithValue(error || 'Failed to fetch Permission data');
        }
    }
);

export const getAllPermissionsDropdown = createAsyncThunk(
    'maintenance/getAllPermissionsDropdown',
    async (_, { rejectWithValue }) => {
        try {
            const response = await apiService.getAllPermissionsDropdown();
            return response;
        } catch (error) {
            return rejectWithValue(error || 'Failed to fetch Permission data');
        }
    }
);

export const deletePermission = createAsyncThunk(
    'users/deletePermission',
    async (permissionId, { rejectWithValue }) => {
        try {
            const response = await apiService.deletePermission(permissionId);
            return response;
        } catch (error) {
            return rejectWithValue(error || 'Failed to delete Permission');
        }
    }
);

export const permissionFetchModule = createAsyncThunk(
    'maintenance/permissionFetchModule',
    async (_, { rejectWithValue }) => {
        try {
            const response = await apiService.getFetchModules();
            return response;
        } catch (error) {
            return rejectWithValue(error || 'Failed to fetch Permission data');
        }
    }
);

export const createPermissionGroup = createAsyncThunk(
    'permissions/createPermissionGroup',
    async (permissionData, { rejectWithValue }) => {
        try {
            const response = await apiService.createPermissionGroup(permissionData);
            return response;
        } catch (error) {
            return rejectWithValue(error || 'Failed to create permission group');
        }
    }
);

export const getSinglePermissionGroup = createAsyncThunk(
    'permissions/getSinglePermissionGroup',
    async (id, { rejectWithValue }) => {
        try {
            const response = await apiService.getSinglePermissionGroup(id);
            return response;
        } catch (error) {
            return rejectWithValue(error || 'Failed to fetch permission group');
        }
    }
);

export const updatePermissionGroup = createAsyncThunk(
    'permissions/updatePermissionGroup',
    async (permissionData, { rejectWithValue }) => {
        try {
            const response = await apiService.updatePermissionGroup(permissionData);
            return response;
        } catch (error) {
            return rejectWithValue(error || 'Failed to update permission group');
        }
    }
);

const initialState = {
    permissionDropdown: [],
    permissionLoading: false,
    deleteLoading: false,
    fetchModulesLoading: false,
    fetchModules: [],
    data: [],
    loading: false,
    error: null,
    currentPage: 1,
    totalPages: 1,
    createLoading: false,
    singlePermissionGroup: null,
    singlePermissionLoading: false,
    updateLoading: false,

    getAllPermissionsDropdown: [],
    getAllPermissionsLoading: false,
    getAllPermissionsError: null,
};

const permissionSlice = createSlice({
    name: 'permissions',
    initialState,
    reducers: {
        setCurrentPage: (state, action) => {
            state.currentPage = action.payload;
        },
        clearError: (state) => {
            state.error = null;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchPermissionData.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchPermissionData.fulfilled, (state, action) => {
                state.loading = false;
                state.data = action.payload?.data?.results || [];
                state.meta = action.payload?.data?.meta || {};
                state.error = null;
            })
            .addCase(fetchPermissionData.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
                state.data = [];
            })
            .addCase(fetchPermissionDropdown.pending, (state) => {
                state.permissionLoading = true;
                state.error = null;
            })
            .addCase(fetchPermissionDropdown.fulfilled, (state, action) => {
                state.permissionLoading = false;
                state.permissionDropdown = action.payload?.data || [];
                state.error = null;
            })
            .addCase(fetchPermissionDropdown.rejected, (state, action) => {
                state.permissionLoading = false;
                state.error = action.payload;
                state.data = [];
            })
            .addCase(deletePermission.pending, (state) => {
                state.deleteLoading = true;
                state.error = null;
            })
            .addCase(deletePermission.fulfilled, (state, action) => {
                state.deleteLoading = false;
                state.error = null;
            })
            .addCase(deletePermission.rejected, (state, action) => {
                state.deleteLoading = false;
                state.error = action.payload;
            })
            .addCase(permissionFetchModule.pending, (state) => {
                state.fetchModulesLoading = true;
                state.error = null;
            })
            .addCase(permissionFetchModule.fulfilled, (state, action) => {
                state.fetchModulesLoading = false;
                state.fetchModules = action.payload?.data || [];
                state.error = null;
            })
            .addCase(permissionFetchModule.rejected, (state, action) => {
                state.fetchModulesLoading = false;
                state.error = action.payload;
                state.data = [];
            })
            .addCase(createPermissionGroup.pending, (state) => {
                state.createLoading = true;
                state.error = null;
            })
            .addCase(createPermissionGroup.fulfilled, (state, action) => {
                state.createLoading = false;
                state.error = null;
            })
            .addCase(createPermissionGroup.rejected, (state, action) => {
                state.createLoading = false;
                state.error = action.payload;
            })
            .addCase(getSinglePermissionGroup.pending, (state) => {
                state.singlePermissionLoading = true;
                state.error = null;
            })
            .addCase(getSinglePermissionGroup.fulfilled, (state, action) => {
                state.singlePermissionLoading = false;
                state.singlePermissionGroup = action.payload?.data || null;
                state.error = null;
            })
            .addCase(getSinglePermissionGroup.rejected, (state, action) => {
                state.singlePermissionLoading = false;
                state.error = action.payload;
            })
            .addCase(updatePermissionGroup.pending, (state) => {
                state.updateLoading = true;
                state.error = null;
            })
            .addCase(updatePermissionGroup.fulfilled, (state, action) => {
                state.updateLoading = false;
                state.error = null;
            })
            .addCase(updatePermissionGroup.rejected, (state, action) => {
                state.updateLoading = false;
                state.error = action.payload;
            })

            .addCase(getAllPermissionsDropdown.pending, (state) => {
                state.getAllPermissionsLoading = true;
                state.getAllPermissionsError = null;
            })
            .addCase(getAllPermissionsDropdown.fulfilled, (state, action) => {
                state.getAllPermissionsLoading = false;
                state.getAllPermissionsDropdown = action.payload?.data || [];
                state.getAllPermissionsError = null;
            })
            .addCase(getAllPermissionsDropdown.rejected, (state, action) => {
                state.getAllPermissionsLoading = false;
                state.getAllPermissionsError = action.payload;
                state.getAllPermissionsDropdown = [];
            });
    },
});

export const { setCurrentPage, clearError } = permissionSlice.actions;

export default permissionSlice.reducer;
